<template>
    <div class="modal-container">
        <form-wrapper class="modal-form" :validator="$v.rateDetail">
            <header class="modal-header">
                <div class="modal-header__right">
                    <md-button class="md-simple md-just-icon md-round" @click.stop="$modal.hide">
                        <md-icon>clear</md-icon>
                    </md-button>
                </div>
            </header>

            <section class="modal-section">
                <div class="section-override">
                    <md-switch v-model="rateDetail.overrideRule">
                       Override
                    </md-switch>
                </div>
                <div class="section-heading">
                    <span class="section-heading__badge">Timeframe</span>
                    <p class="section-heading__copy">Choose when the rule is active.</p>
                </div>
                <div class="section-card section-card--wide">
                    <rule-valid-days
                        :current-days="rateDetail.validForWeekDays"
                        :from-date="rateDetail.validFromDate"
                        :to-date="rateDetail.validToDate"
                        @daysSelected="handleDaysSeleceted"
                        ref="ruleValidDays"
                    />
                </div>
            </section>

            <section class="modal-section">
                <div class="section-heading">
                    <span class="section-heading__badge">Charging</span>
                    <p class="section-heading__copy">Set flag fall, interval pricing and inclusions.</p>
                </div>
                <div class="section-card section-card--compact">
                    <div class="charging-grid">
                        <div class="charging-group">
                            <h5 class="charging-group__title">Flag fall</h5>
                            <p class="charging-group__hint">Optional one-off amount before interval billing begins.</p>
                            <form-group name="volumeFlagfall" label="Flag Fall">
                                <rule-operator
                                    v-if="$_nullChecker(rateDetail.volumeFlagfall)"
                                    v-model="rateDetail.volumeFlagfall.operator"
                                    :value="rateDetail.volumeFlagfall.operator"
                                />
                                <md-input v-model.number="rateDetail.volumeFlagfall.value" />
                            </form-group>
                        </div>

                        <div class="charging-separator" aria-hidden="true"></div>

                        <div class="charging-group">
                            <h5 class="charging-group__title">Intervals</h5>
                            <p class="charging-group__hint">Breakpoints that trigger per volume pricing.</p>
                            <div class="field-pair">
                                <form-group name="volumeInterval" label="Volume Interval">
                                    <rule-operator
                                        v-if="$_nullChecker(rateDetail.volumeInterval)"
                                        v-model="rateDetail.volumeInterval.operator"
                                        :value="rateDetail.volumeInterval.operator"
                                    />
                                    <md-input v-model.number="rateDetail.volumeInterval.value" />
                                </form-group>
                                <form-group name="volumeRatePerInterval" label="Charge Per Interval">
                                    <rule-operator
                                        v-if="$_nullChecker(rateDetail.volumeRatePerInterval)"
                                        v-model="rateDetail.volumeRatePerInterval.operator"
                                        :value="rateDetail.volumeRatePerInterval.operator"
                                    />
                                    <md-input v-model.number="rateDetail.volumeRatePerInterval.value" />
                                </form-group>
                            </div>
                        </div>

                        <div class="charging-separator" aria-hidden="true"></div>

                        <div class="charging-group">
                            <h5 class="charging-group__title">Inclusions</h5>
                            <p class="charging-group__hint">Define included volume and minimum charges.</p>
                            <div class="field-pair">
                                <form-group name="includedVolume" label="Included Volume">
                                    <rule-operator
                                        v-if="$_nullChecker(rateDetail.includedVolume)"
                                        v-model="rateDetail.includedVolume.operator"
                                        :value="rateDetail.includedVolume.operator"
                                    />
                                    <md-input v-model.number="rateDetail.includedVolume.value" />
                                </form-group>
                                <form-group name="volumeMinimumCharge" label="Minimum Charge">
                                    <rule-operator
                                        v-if="$_nullChecker(rateDetail.volumeMinimumCharge)"
                                        v-model="rateDetail.volumeMinimumCharge.operator"
                                        :value="rateDetail.volumeMinimumCharge.operator"
                                    />
                                    <md-input v-model.number="rateDetail.volumeMinimumCharge.value" />
                                </form-group>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <footer class="modal-footer">
                <div class="modal-footer__actions">
                    <md-button class="md-default dialog-button" @click.stop="$modal.hide">Cancel</md-button>
                    <md-button class="md-primary dialog-button" @click="create()">
                        {{ isUpdate ? 'Update' : 'Create' }}
                    </md-button>
                </div>
            </footer>
        </form-wrapper>
    </div>
</template>

<script>
import { RATE_RULE } from '@/utils/defaults';
import { GeneralMixin, RatesEngineMixin } from '@/mixins';
import { handleRequests, showErrorMessage } from '@/helpers';
import { mapGetters } from 'vuex';
import { RATE_CHARGING_TYPES } from '@/utils/constants';
import { decimal } from 'vuelidate/lib/validators';
import { RuleValidDays } from '@/components';
import RuleOperator from './RuleOperator';

export default {
    name: 'CreateCubicRuleContent',
    components: {
        RuleValidDays,
        RuleOperator
    },
    mixins: [GeneralMixin, RatesEngineMixin],
    props: {
        rateDetail: {
            type: Object,
            default: () => Object.assign({}, RATE_RULE()),
        },
        resolve: {
            type: Function,
            default: () => {}
        },
        listLength: {
            type: Number,
            default: 0
        },
        isUpdate: {
            type: Boolean,
            default: false
        },
        customerId: {
            type: Number,
            default: null
        },
        rateGroupId: {
            type: Number,
            default: null
        },
        driverUserId: {
            type: Number,
            default: null
        },
        driverRuleCreation: {
            type: Boolean,
            default: false
        },
        teamRegionId: {
            type: Number,
            default: null
        }
    },
    created() {
        this.$_ensureChargeField('volumeFlagfall');
        this.$_ensureChargeField('volumeInterval');
        this.$_ensureChargeField('volumeRatePerInterval');
        this.$_ensureChargeField('includedVolume');
        this.$_ensureChargeField('volumeMinimumCharge');

    },
    data() {
        return {
            ruleData: {
                type: null,
                rule: 'test'
            },
            rateChargingTypes: RATE_CHARGING_TYPES
        };
    },
    validations: {
        rateDetail: {
            volumeFlagfall: {
                value: {
                    decimal
                }
            },
            volumeInterval: {
                value: {
                    decimal
                }
            },
            volumeRatePerInterval: {
                value: {
                    decimal
                }
            },
            includedVolume: {
                value: {
                    decimal
                }
            },
            volumeMinimumCharge: {
                value: {
                    decimal
                }
            },
        }
    },
    computed: {
        ...mapGetters({
            user: 'user/user'
        })
    },
    methods: {
        validateData() {
            if (this.$v.$invalid) {
                return false;
            }

            if (
                this.$v.rateDetail.volumeFlagfall.value.$model === null &&
                this.$v.rateDetail.volumeInterval.value.$model === null &&
                this.$v.rateDetail.volumeMinimumCharge.value.$model === null &&
                this.$v.rateDetail.volumeRatePerInterval.value.$model === null &&
                this.$v.rateDetail.includedVolume.value.$model === null
            ) {
                return false;
            }

            return true;
        },
        async create() {
            const ruleDaysValid = this.$refs.ruleValidDays.validate();
            if (!ruleDaysValid) {
                return;
            }

            if (this.isUpdate) {
                this.updateRuleDetails();
                return;
            }

            if (!this.validateData()) {
                showErrorMessage(this, 'Rule must have valid data', null);
                return;
            }

            this.$_handleLoaderState(true, 'PROCESSING...');
            const chargeType = this.rateChargingTypes.find((x) => x.key.toLowerCase() === this.$route.params.type);
            const obj = this.$_getRateRuleData(
                chargeType,
                this.user.publicUserId,
                this.rateDetail,
                this.customerId,
                this.listLength,
                this.rateGroupId,
                this.driverRuleCreation,
                this.driverUserId,
                this.teamRegionId
            );

            const endpoint = '/api/rates/create';
            const payload = {
                method: 'post',
                data: obj
            };

            try {
                const response = await handleRequests(endpoint, payload);

                if (response.status === 200) {
                    this.$notify({
                        message: 'Successfully created rule.',
                        type: 'success'
                    });

                    this.resolve({
                        record: response.data
                    });
                } else {
                    showErrorMessage(this, 'Failed creating rule.', null);
                }
            } catch (error) {
                showErrorMessage(this, error.data, null);
            }

            this.$_handleLoaderState(false);
        },
        async updateRuleDetails() {
            if (!this.validateData()) {
                showErrorMessage(this, 'Rule must have valid data', null);
                return;
            }

            this.$_handleLoaderState(true, 'UPDATING...');

            const rateId = this.rateDetail.rateRulesId;
            this.rateDetail.condition = null;
            this.rateDetail.currency = null;
            this.rateDetail.priority = null;
            this.rateDetail.rateRulesId = null;

            const obj = {
                rateRulesId: rateId,
                rule: JSON.stringify(this.rateDetail)
            };

            const endpoint = '/api/rates/updateRate';
            const payload = {
                method: 'post',
                data: obj
            };

            try {
                const response = await handleRequests(endpoint, payload);

                if (response.status === 200) {
                    this.$notify({
                        message: 'Successfully updated rule.',
                        type: 'success'
                    });

                    this.resolve({
                        record: response.status
                    });
                } else {
                    showErrorMessage(this, 'Failed updating rule.', null);
                }
            } catch (error) {
                showErrorMessage(this, 'Failed updating rule.', null);
            }

            this.$_handleLoaderState(false);
        },
        handleDaysSeleceted(value) {
            this.rateDetail = Object.assign(this.rateDetail, value);
        }
    }
};
</script>

<style lang="scss" scoped>
@import './create-rule-content.shared.scss';
</style>
